/**
 * App Configuration Loader
 *
 * Carica la configurazione dell'applicazione da un tag <script type="application/json">
 * e la rende disponibile globalmente tramite window.AppConfig
 */
(function(window) {
    'use strict';

    let configCache = null;
    const CONFIG_ELEMENT_ID = 'app-config';

    /**
     * Fallback configuration in caso di errore
     */
    const defaultConfig = {
        endpoints: {},
        i18n: {
            loading: 'Loading...'
        },
        modules: {},
        user: {
            isAuthenticated: false
        }
    };

    /**
     * Carica e parsa la configurazione dal DOM
     * @returns {Object} La configurazione parsata
     */
    function loadConfig() {
        // Se già caricato, usa la cache
        if (configCache !== null) {
            return configCache;
        }

        const configElement = document.getElementById(CONFIG_ELEMENT_ID);

        // Se l'elemento non esiste, usa il default
        if (!configElement) {
            console.warn('[AppConfig] Config element #' + CONFIG_ELEMENT_ID + ' not found, using defaults');
            configCache = defaultConfig;
            return configCache;
        }

        try {
            // Parse del JSON
            configCache = JSON.parse(configElement.textContent);

            // Rimuovi l'elemento dal DOM per pulire l'HTML (opzionale ma consigliato)
            configElement.parentNode.removeChild(configElement);

        } catch (error) {
            console.error('[AppConfig] Failed to parse configuration:', error);
            configCache = defaultConfig;
        }

        return configCache;
    }

    /**
     * Ottiene un endpoint specifico
     * @param {string} module - Nome del modulo (es. 'cart', 'wishlist')
     * @param {string} key - Chiave dell'endpoint (es. 'shoppingCart', 'addItem')
     * @returns {string} L'URL dell'endpoint o stringa vuota
     */
    function getEndpoint(module, key) {
        const config = loadConfig();

        if (config.endpoints && config.endpoints[module] && config.endpoints[module][key]) {
            return config.endpoints[module][key];
        }

        console.warn('[AppConfig] Endpoint not found: ' + module + '.' + key);
        return '';
    }

    /**
     * Ottiene una traduzione
     * @param {string} key - Chiave della traduzione
     * @returns {string} La traduzione o la chiave stessa se non trovata
     */
    function getTranslation(key) {
        const config = loadConfig();

        if (config.i18n && config.i18n[key]) {
            return config.i18n[key];
        }

        console.warn('[AppConfig] Translation not found: ' + key);
        return key;
    }

    /**
     * Ottiene la configurazione di un modulo
     * @param {string} moduleName - Nome del modulo (es. 'cart', 'wishlist')
     * @param {string} [key] - Chiave specifica del parametro (opzionale)
     * @returns {*} La configurazione del modulo o un parametro specifico
     *
     * @example
     * // Ottieni tutta la configurazione del modulo cart
     * AppConfig.getModuleConfig('cart');
     * // Returns: { miniCartEnabled: true, withinShoppingCart: false }
     *
     * @example
     * // Ottieni un parametro specifico
     * AppConfig.getModuleConfig('cart', 'miniCartEnabled');
     * // Returns: true
     */
    function getModuleConfig(moduleName, key) {
        const config = loadConfig();

        // Verifica se il modulo esiste
        if (!config.modules || !config.modules[moduleName]) {
            console.warn('[AppConfig] Module config not found: ' + moduleName);
            return key ? undefined : {};
        }

        // Se non è specificata una chiave, ritorna tutta la configurazione del modulo
        if (!key) {
            return config.modules[moduleName];
        }

        // Ritorna il parametro specifico
        if (config.modules[moduleName][key] !== undefined) {
            return config.modules[moduleName][key];
        }

        console.warn('[AppConfig] Module config key not found: ' + moduleName + '.' + key);
        return undefined;
    }

    /**
     * Verifica se un modulo è attivo
     * @param {string} moduleName - Nome del modulo
     * @returns {boolean} True se il modulo esiste nella configurazione
     */
    function isModuleActive(moduleName) {
        const config = loadConfig();
        return config.modules && config.modules[moduleName] !== undefined;
    }

    /**
     * Verifica se l'utente è autenticato
     * @returns {boolean} True se l'utente è autenticato
     */
    function isAuthenticated() {
        const config = loadConfig();
        return config.user && config.user.isAuthenticated === true;
    }

    /**
     * Ottiene l'intera configurazione
     * @returns {Object} La configurazione completa
     */
    function getConfig() {
        return loadConfig();
    }

    /**
     * Resetta la cache (utile per testing)
     */
    function resetCache() {
        configCache = null;
    }

    // Espone l'API pubblica su window
    window.AppConfig = {
        load: loadConfig,
        get: getConfig,
        getEndpoint: getEndpoint,
        getTranslation: getTranslation,
        getModuleConfig: getModuleConfig,
        isModuleActive: isModuleActive,
        isAuthenticated: isAuthenticated,
        reset: resetCache
    };

})(window);
